import type { PropertyDefinition } from "@shared/schema";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { randomOptionColor } from "@/lib/property-colors";
import { rpcClient } from "@/lib/rpc";

type UpdatePropertyDefinitionResult = Awaited<
  ReturnType<typeof rpcClient.jobOpenings.updatePropertyDefinition>
>;

/** The backend returns this instead of applying when removing an option would
 * erase values from candidaturas and `force` was not set. */
function isConfirmationRequired(
  res: UpdatePropertyDefinitionResult,
): res is { requiresConfirmation: true; affectedCount: number } {
  return !!res && typeof res === "object" && "requiresConfirmation" in res;
}

/**
 * Shared hook for CRUD mutations on property definition options.
 * Used by both property-config-sheet (manage options without candidates)
 * and property-value-cell (manage options inline while editing values).
 *
 * Deleting an option cascades to erase that value from every candidatura, so the
 * backend blocks it with `requiresConfirmation` + a count. The hook surfaces that
 * as `pendingRemoval`; consumers render a confirm dialog and call `confirmRemoval`
 * to resend with `force`.
 */
export function usePropertyOptions(
  definition: Pick<PropertyDefinition, "id" | "jobOpeningId" | "options">,
  queryKey: unknown[],
) {
  const queryClient = useQueryClient();
  const options = definition.options ?? [];
  const [pendingRemoval, setPendingRemoval] = useState<{
    optionId: string;
    affectedCount: number;
  } | null>(null);

  const patchOptions = (updatedOptions: typeof options, force = false) =>
    rpcClient.jobOpenings.updatePropertyDefinition({
      id: definition.jobOpeningId,
      defId: definition.id,
      options: updatedOptions,
      force,
    });

  const addOption = useMutation({
    mutationFn: (label: string) => {
      const newOption = { id: crypto.randomUUID(), label, color: randomOptionColor() };
      return patchOptions([...options, newOption]);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  const updateOption = useMutation({
    mutationFn: ({
      optionId,
      label,
      color,
    }: {
      optionId: string;
      label?: string;
      color?: string;
    }) =>
      patchOptions(
        options.map((o) =>
          o.id === optionId
            ? {
                ...o,
                ...(label !== undefined ? { label } : {}),
                ...(color !== undefined ? { color } : {}),
              }
            : o,
        ),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  const deleteOption = useMutation({
    mutationFn: async ({ optionId, force = false }: { optionId: string; force?: boolean }) => {
      const result = await patchOptions(
        options.filter((o) => o.id !== optionId),
        force,
      );
      return { result, optionId };
    },
    onSuccess: ({ result, optionId }) => {
      if (isConfirmationRequired(result)) {
        setPendingRemoval({ optionId, affectedCount: result.affectedCount });
        return;
      }
      setPendingRemoval(null);
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const confirmRemoval = () => {
    if (pendingRemoval) deleteOption.mutate({ optionId: pendingRemoval.optionId, force: true });
  };
  const cancelRemoval = () => setPendingRemoval(null);

  return {
    options,
    addOption,
    updateOption,
    deleteOption,
    pendingRemoval,
    confirmRemoval,
    cancelRemoval,
  };
}
